<script setup lang="ts">
// ============================================================================
// Imports
// ============================================================================
import { watch, onUnmounted } from 'vue'
import { updatePageBranding, resetPageBranding } from '@/utils/helpers/branding'
import type { VerificationResponse } from '@/interfaces/verification'

// ============================================================================
// Props
// ============================================================================
interface Props {
  verification?: VerificationResponse | null
  // Optional custom title and icon for special cases (e.g., fraud report)
  customTitle?: string
  customIcon?: string
}

const props = withDefaults(defineProps<Props>(), {
  verification: null,
  customTitle: undefined,
  customIcon: undefined
})

// ============================================================================
// Event Handlers
// ============================================================================

/**
 * Handles logo image loading for smooth fade-in appearance
 * Removes opacity-0 class and adds opacity-100 when image loads
 */
const onLogoLoad = (event: Event) => {
  const img = event.target as HTMLImageElement
  img.classList.remove('opacity-0')
  img.classList.add('opacity-100')
}

// ============================================================================
// Watchers
// ============================================================================

/**
 * Watches for verification data changes to update page branding
 * Applies custom branding if available, otherwise resets to default
 */
watch(
  () => props.verification,
  (newVerification) => {
    if (newVerification?.page_template_content?.branding) {
      updatePageBranding(newVerification.page_template_content.branding)
    } else {
      resetPageBranding()
    }
  },
  { immediate: true }
)

// ============================================================================
// Lifecycle Hooks
// ============================================================================

/**
 * Resets branding when component is unmounted
 */
onUnmounted(() => {
  resetPageBranding()
})
</script>

<template>
  <div class="bg-linear-to-br from-blue-600 to-primary-500">
    <!-- ===================================================================== -->
    <!-- Custom Branding Section -->
    <!-- ===================================================================== -->
    <!-- Displays custom branding (logo, title, subheading) if available -->
    <div
      v-if="verification?.page_template_content?.branding && !customTitle"
      class="container mx-auto px-4 py-8"
    >
      <div class="text-center max-w-2xl mx-auto">
        <!-- Brand Logo -->
        <div
          class="inline-flex items-center justify-center w-14 h-14 bg-white rounded-lg shadow-xs mb-3 transition-all duration-300 hover:shadow-md"
        >
          <img
            :src="verification.page_template_content.branding.logo"
            :alt="verification.page_template_content.branding.title"
            class="w-10 h-10 object-contain transition-all duration-300 opacity-0 brand-logo"
            @load="onLogoLoad"
          />
        </div>

        <!-- Brand Title and Subheading -->
        <h1 class="text-xl font-bold text-white mb-1">
          {{ verification.page_template_content.branding.title }}
        </h1>
        <p class="text-sm text-white/90 mb-4">
          {{ verification.page_template_content.branding.subheading }}
        </p>
      </div>
    </div>

    <!-- ===================================================================== -->
    <!-- Default Branding Section -->
    <!-- ===================================================================== -->
    <!-- Displays default branding when no custom branding is available -->
    <div v-else class="container mx-auto px-4 py-8 text-center">
      <div class="inline-flex items-center bg-white/10 backdrop-blur-xs px-4 py-2 rounded-lg">
        <!-- Custom Icon or Default Shield Icon -->
        <img
          v-if="customIcon"
          :src="customIcon"
          alt=""
          class="w-5 h-5 mr-2 brightness-0 invert"
        />
        <img
          v-else
          src="@/assets/img/shield-check.svg"
          alt="Verification Shield"
          class="w-5 h-5 mr-2 brightness-0 invert"
        />
        <h1 class="text-lg font-semibold text-white">
          <span v-if="verification?.has_demo_domain && !customTitle">Demo</span>
          {{ customTitle || 'Document Details' }}
        </h1>
      </div>
    </div>
  </div>
</template>

<!-- ===================================================================== -->
<!-- Component Styles -->
<!-- ===================================================================== -->
<style scoped lang="postcss">
/* Brand logo fade-in and hover effects */
.brand-logo {
  transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out;
}

.brand-logo:hover {
  transform: scale(1.05);
}
</style>

